import { DeleteIcon } from '@blocksuite/icons/rc';
import { Tooltip, useConfirmModal } from '@pique/component';
import {
  type ActionItemSelectionState,
  type GroupedReorderEvent,
  SortableActionItemsPanel,
} from '@pique/core/components/action-items/SortableActionItemsPanel';
import type { ActionItemGroup } from '@pique/core/modules/properties/services/action-items-service';
import {
  ActionItemsService,
  NO_DUE_DATE_KEY,
} from '@pique/core/modules/properties/services/action-items-service';
import {
  ACTION_ITEM_STATUS,
  type ActionItemGroupBy,
  type ActionItemPriority,
  type ActionItemStatus,
  type ActionItemWhere,
  type SerializedTodoItem,
} from '@pique/core/modules/properties/services/schema';
import { WorkbenchService } from '@pique/core/modules/workbench';
import { posthog } from '@pique/core/posthog';
import { useLiveData, useService } from '@toeverything/infra';
import { useAtom, useAtomValue } from 'jotai';
import type React from 'react';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import { ActionItemsFocusChartLive } from '@pique/core/components/action-items/charts/ActionItemsFocusChartLive';
import { enablePicoFocusModeAtom } from '../../../../../components/pique/setting-modal/atoms';

import { actionItemsHeaderModeAtom } from '../../../../../atoms';
import { ActionItemsLoading } from './action-items-states';
import * as styles from './ActionItemsPanel.css';
import {
  CompletionFilter,
  type CompletionFilterType,
} from './CompletionFilter';
import type { ViewMode } from './ViewModeSelector';

const LazyAddActionItemInline = lazy(() =>
  import('./AddActionItemInline').then(module => ({
    default: module.AddActionItemInline,
  }))
);

const LazyViewModeSelector = lazy(() =>
  import('./ViewModeSelector').then(module => ({
    default: module.ViewModeSelector,
  }))
);

const LazyKanbanBoard = lazy(() =>
  import('@pique/core/components/action-items/KanbanBoard').then(module => ({
    default: module.KanbanBoard,
  }))
);

const LazyPicoFocusView = lazy(() =>
  import('./PicoFocusView').then(module => ({
    default: module.PicoFocusView,
  }))
);

const ViewModeSelectorLoading = () => (
  <div
    style={{
      width: 100,
      height: 24,
      borderRadius: 4,
      backgroundColor: 'var(--affine-border-color)',
      opacity: 0.3,
    }}
  />
);

const TruncationIndicator: React.FC<{ hiddenCount: number }> = ({
  hiddenCount,
}) => {
  return (
    <div className={styles.truncationIndicator}>
      <div className={styles.truncationIndicatorLine} />
      <div className={styles.truncationIndicatorContent}>
        <span className={styles.truncationIndicatorText}>
          {hiddenCount} action item{hiddenCount === 1 ? '' : 's'} hidden
        </span>
      </div>
      <div className={styles.truncationIndicatorLine} />
    </div>
  );
};

export interface ActionItemsPanelProps {
  assignedToEmails?: string[];
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  showViewModeSelector?: boolean;
}

interface OptimisticPanelData {
  sourceKey: symbol;
  groups: ActionItemGroup[];
  flatRankItems: SerializedTodoItem[];
}

export const ActionItemsPanel: React.FC<ActionItemsPanelProps> = ({
  assignedToEmails,
  viewMode: controlledViewMode,
  onViewModeChange: onControlledViewModeChange,
  showViewModeSelector = true,
}) => {
  const actionItemsService = useService(ActionItemsService);
  const actionItemsByPage = useLiveData(actionItemsService.actionItems$);
  const workbenchService = useService(WorkbenchService);
  const { openConfirmModal } = useConfirmModal();
  const [headerMode, setHeaderMode] = useAtom(actionItemsHeaderModeAtom);
  const picoFocusModeEnabled = useAtomValue(enablePicoFocusModeAtom);

  const [loading, setLoading] = useState(true);
  const [internalViewMode, setInternalViewMode] = useState<ViewMode>('pageId');
  const viewMode = controlledViewMode ?? internalViewMode;
  const setViewMode = onControlledViewModeChange ?? setInternalViewMode;
  const [completionFilter, setCompletionFilter] =
    useState<CompletionFilterType>('open');
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const [optimisticPanelData, setOptimisticPanelData] =
    useState<OptimisticPanelData | null>(null);
  const lastFocusedCountRef = useRef(0);
  // Render only the most recent groups initially (keeps DOM small for large workspaces)
  const INITIAL_VISIBLE_GROUPS = 5;
  const GROUP_PAGE_SIZE = 5;
  const [visibleGroupCount, setVisibleGroupCount] = useState(
    INITIAL_VISIBLE_GROUPS
  );
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const groupsLengthRef = useRef(0);
  const groupsRef = useRef<ActionItemGroup[]>([]);
  const flatRankItemsRef = useRef<SerializedTodoItem[]>([]);
  const assignedToEmailsKey = useMemo(
    () => (assignedToEmails?.length ? assignedToEmails.join('|') : ''),
    [assignedToEmails]
  );
  const orderBy = useMemo(
    () => [
      { field: 'priorityRank' as const, direction: 'asc' as const },
      { field: 'createdAt' as const, direction: 'desc' as const },
    ],
    []
  );

  const focusedItems = useMemo(() => {
    const focusedWhere: ActionItemWhere = { status: ACTION_ITEM_STATUS.IN_PROGRESS };
    if (assignedToEmails?.length) {
      focusedWhere.assignedTo = assignedToEmails;
    }
    return actionItemsService.query({
      where: focusedWhere,
      orderBy,
    });
  }, [actionItemsService, actionItemsByPage, assignedToEmailsKey, orderBy]);
  const focusModeEnabled = focusedItems.length > 0;

  useEffect(() => {
    return () => {
      setHeaderMode('addItem');
    };
  }, [setHeaderMode]);


  const isDueDateMode = viewMode === 'dueDate';
  const isMeetingMode = viewMode === 'pageId';
  const isKanbanMode = viewMode === 'kanban';
  const activeFocusItemId = useLiveData(actionItemsService.focusedItemId$);
  const isPicoFocusActive = picoFocusModeEnabled && activeFocusItemId !== null;

  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const selectionActive = selectedIds.length > 0;

  const selection: ActionItemSelectionState = useMemo(
    () => ({
      selectable: selectionActive,
      selectedIds,
      onSelectedIdsChange: setSelectedIds,
    }),
    [selectionActive, selectedIds]
  );

  useEffect(() => {
    const isFocusStepActive =
      document.body.dataset['onboardingStep'] === 'focus-mode-drag';
    if (!isFocusStepActive) {
      lastFocusedCountRef.current = focusedItems.length;
      return;
    }
    if (focusedItems.length > lastFocusedCountRef.current) {
      window.dispatchEvent(new CustomEvent('pique-focus-mode-item-added'));
    }
    lastFocusedCountRef.current = focusedItems.length;
  }, [focusedItems.length]);

  const derivedData = useMemo(() => {
    const completedFilter =
      completionFilter === 'open'
        ? false
        : completionFilter === 'closed'
          ? true
          : undefined;

    const whereClause: ActionItemWhere | undefined = (() => {
      const where: ActionItemWhere = {};
      if (completedFilter !== undefined) where.completed = completedFilter;
      if (assignedToEmails?.length) where.assignedTo = assignedToEmails;
      return Object.keys(where).length ? where : undefined;
    })();

    const isInProgress = (item: SerializedTodoItem) =>
      item.status === ACTION_ITEM_STATUS.IN_PROGRESS;

    const allQueried = actionItemsService.query({
      where: whereClause,
      orderBy,
    });
    const flat = isKanbanMode
      ? allQueried
      : allQueried.filter(item => !isInProgress(item));

    const countWhere: ActionItemWhere | undefined = assignedToEmails?.length
      ? { assignedTo: assignedToEmails }
      : undefined;

    const allItems = actionItemsService.query({ where: countWhere });
    const completedCount = allItems.filter(
      (item: SerializedTodoItem) =>
        item.completedAt || (item.progress && item.progress >= 100)
    ).length;

    const stripInProgress = (groups: ActionItemGroup[]) =>
      groups
        .map((g: ActionItemGroup) => ({
          ...g,
          items: g.items.filter(item => !isInProgress(item)),
        }))
        .filter((g: ActionItemGroup) => g.items.length > 0);

    const nextGroups = isKanbanMode
      ? []
      : (() => {
          const actionItemGroups = actionItemsService.queryWithGrouping({
            where: whereClause,
            groupBy: viewMode as ActionItemGroupBy,
            orderBy,
          });
          const filtered = stripInProgress(actionItemGroups);
          if (!isDueDateMode) return filtered;
          return filtered.slice().sort((a: ActionItemGroup, b: ActionItemGroup) => {
            if (a.id === NO_DUE_DATE_KEY) return 1;
            if (b.id === NO_DUE_DATE_KEY) return -1;
            return a.id.localeCompare(b.id);
          });
        })();

    return {
      groups: nextGroups,
      flatRankItems: flat,
      itemCounts: {
        total: allItems.length,
        completed: completedCount,
        open: allItems.length - completedCount,
      },
    };
  }, [
    actionItemsService,
    actionItemsByPage,
    assignedToEmails,
    completionFilter,
    isDueDateMode,
    isKanbanMode,
    orderBy,
    viewMode,
  ]);

  const renderDataKey = useMemo(
    () => Symbol('action-items-panel-data'),
    [actionItemsByPage, assignedToEmailsKey, completionFilter, viewMode]
  );
  const usingOptimisticPanelData =
    optimisticPanelData?.sourceKey === renderDataKey;
  const groups = usingOptimisticPanelData
    ? optimisticPanelData.groups
    : derivedData.groups;
  const flatRankItems = usingOptimisticPanelData
    ? optimisticPanelData.flatRankItems
    : derivedData.flatRankItems;
  const itemCounts = derivedData.itemCounts;
  const setOptimisticPanelSnapshot = useCallback(
    (nextGroups: ActionItemGroup[], nextFlatRankItems: SerializedTodoItem[]) => {
      setOptimisticPanelData({
        sourceKey: renderDataKey,
        groups: nextGroups,
        flatRankItems: nextFlatRankItems,
      });
    },
    [renderDataKey]
  );

  useEffect(() => {
    setLoading(false);
  }, [renderDataKey]);

  useEffect(() => {
    if (
      optimisticPanelData &&
      optimisticPanelData.sourceKey !== renderDataKey
    ) {
      setOptimisticPanelData(null);
    }
  }, [optimisticPanelData, renderDataKey]);

  // Reset pagination when the query inputs change.
  useEffect(() => {
    setVisibleGroupCount(INITIAL_VISIBLE_GROUPS);
  }, [viewMode, completionFilter, assignedToEmailsKey]);

  // Keep refs in sync for stable callbacks, and clamp visible count if groups shrink.
  useEffect(() => {
    groupsLengthRef.current = groups.length;
    groupsRef.current = groups;
    flatRankItemsRef.current = flatRankItems;
    setVisibleGroupCount(prev => Math.min(prev, groups.length || prev));
  }, [flatRankItems, groups]);

  const loadMoreGroups = useCallback(() => {
    setVisibleGroupCount(prev =>
      Math.min(prev + GROUP_PAGE_SIZE, groupsLengthRef.current)
    );
  }, []);

  const hasMoreGroups = visibleGroupCount < groups.length;

  // Auto-load more groups as the user scrolls near the bottom (no virtualization,
  // so DnD context remains intact).
  useEffect(() => {
    if (!hasMoreGroups) return;
    const root = scrollViewportRef.current;
    const target = loadMoreSentinelRef.current;
    if (!root || !target) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          loadMoreGroups();
        }
      },
      { root, rootMargin: '600px 0px' }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMoreGroups, loadMoreGroups]);

  const handleViewModeChange = useCallback(
    (newMode: ViewMode) => {
      setViewMode(newMode);
      posthog.capture('action_items_sort_changed', { sort_mode: newMode });
    },
    [setViewMode]
  );

  const handleCompletionFilterChange = useCallback(
    (newFilter: CompletionFilterType) => {
      setCompletionFilter(newFilter);
      posthog.capture('action_items_filter_changed', { filter_type: newFilter });
    },
    []
  );

  const handleClearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const handleBulkToggleComplete = useCallback(() => {
    if (selectedIds.length === 0) return;

    const isCompletingItems = completionFilter === 'open';
    const newStatus = isCompletingItems
      ? (ACTION_ITEM_STATUS.DONE as ActionItemStatus)
      : (ACTION_ITEM_STATUS.IN_PROGRESS as ActionItemStatus);
    const newProgress = isCompletingItems ? 100 : 0;

    selectedIds.forEach(id => {
      actionItemsService.updateById(id, {
        status: newStatus,
        progress: newProgress,
      });
    });

    setSelectedIds([]);
  }, [actionItemsService, completionFilter, selectedIds]);

  const handleBulkDelete = useCallback(() => {
    if (selectedIds.length === 0) return;

    const count = selectedIds.length;
    openConfirmModal({
      title: 'Delete action items',
      description: `This moves ${count} action item${count === 1 ? '' : 's'} to trash so ${count === 1 ? 'it' : 'they'} can be restored.`,
      confirmText: 'Delete',
      confirmButtonOptions: {
        variant: 'error',
      },
      onConfirm: () => {
        selectedIds.forEach(id => {
          actionItemsService.removeById(id);
        });
        setSelectedIds([]);
      },
    });
  }, [actionItemsService, openConfirmModal, selectedIds]);

  const clearFocusedItemIf = useCallback(
    (shouldClear: (activeId: string) => boolean) => {
      try {
        const activeId = actionItemsService.focusedItemId$.value;
        if (activeId && shouldClear(activeId)) {
          actionItemsService.setFocusedItem(null);
        }
      } catch {}
    },
    [actionItemsService]
  );

  const handleSetFocusedItems = useCallback(
    (newFocusedItems: SerializedTodoItem[]) => {
      const prevIds = focusedItems.map((i: SerializedTodoItem) => i.id);
      const nextIds = newFocusedItems.map((i: SerializedTodoItem) => i.id);
      const isUnchanged =
        prevIds.length === nextIds.length &&
        prevIds.every((id: string, idx: number) => id === nextIds[idx]);
      if (isUnchanged) return;

      actionItemsService.beginBatch();
      try {
        const newIds = new Set(newFocusedItems.map((i: SerializedTodoItem) => i.id));
        const currentIds = new Set(focusedItems.map((i: SerializedTodoItem) => i.id));

        const toAdd = newFocusedItems.filter(
          (i: SerializedTodoItem) => !currentIds.has(i.id)
        );
        if (toAdd.length > 0) {
          const addedIds = new Set(toAdd.map(item => item.id));
          const nextGroups = groupsRef.current
            .map((group: ActionItemGroup) => ({
              ...group,
              items: group.items.filter(item => !addedIds.has(item.id)),
            }))
            .filter((group: ActionItemGroup) => group.items.length > 0);
          const nextFlatRankItems = flatRankItemsRef.current.filter(
            item => !addedIds.has(item.id)
          );
          setOptimisticPanelSnapshot(nextGroups, nextFlatRankItems);
        }
        toAdd.forEach((item: SerializedTodoItem) => {
          actionItemsService.updateById(item.id, {
            isFocused: true,
            status: ACTION_ITEM_STATUS.IN_PROGRESS as ActionItemStatus,
          });
        });

        const toRemove = focusedItems.filter(
          (i: SerializedTodoItem) => !newIds.has(i.id)
        );
        toRemove.forEach((item: SerializedTodoItem) => {
          actionItemsService.updateById(item.id, {
            isFocused: false,
            status: ACTION_ITEM_STATUS.TODO as ActionItemStatus,
          });
        });

        clearFocusedItemIf(activeId => !newIds.has(activeId));

        if (newFocusedItems.length > 0) {
          const ranks = newFocusedItems
            .map(i => i.priorityRank ?? 0)
            .sort((a, b) => a - b);

          newFocusedItems.forEach((item, index) => {
            const newRank = ranks[index];
            if (item.priorityRank !== newRank) {
              actionItemsService.updateById(item.id, { priorityRank: newRank });
            }
          });
        }
      } finally {
        actionItemsService.endBatch();
      }
    },
    [actionItemsService, clearFocusedItemIf, focusedItems, setOptimisticPanelSnapshot]
  );

  const handleItemCompletion = useCallback(
    (completedItemId: string) => {
      clearFocusedItemIf(activeId => activeId === completedItemId);
    },
    [clearFocusedItemIf]
  );

  const handleGroupedReorder = useCallback(
    (event: GroupedReorderEvent) => {
      let nextGroups = event.nextGroups;
      actionItemsService.beginBatch();
      try {

        // If item moved across groups, update its grouped property.
        if (event.fromGroupId !== event.toGroupId) {
          const toGroup = event.nextGroups.find(g => g.id === event.toGroupId);
          const groupValue = toGroup?.metadata?.groupValue;

          if (event.groupBy === 'dueDate') {
            const updates = {
              dueDate: typeof groupValue === 'string' ? groupValue : null,
            } satisfies Partial<SerializedTodoItem>;
            nextGroups = nextGroups.map(g => ({
              ...g,
              items: g.items.map(it =>
                it.id === event.itemId ? { ...it, ...updates } : it
              ),
            }));
            actionItemsService.updateById(event.itemId, updates);
          } else if (event.groupBy === 'status') {
            const updates = {
              status:
                typeof groupValue === 'string'
                  ? (groupValue as ActionItemStatus)
                  : undefined,
            } satisfies Partial<SerializedTodoItem>;
            nextGroups = nextGroups.map(g => ({
              ...g,
              items: g.items.map(it =>
                it.id === event.itemId ? { ...it, ...updates } : it
              ),
            }));
            actionItemsService.updateById(event.itemId, updates);
          } else if (event.groupBy === 'priority') {
            const newPriority =
              typeof groupValue === 'string'
                ? (groupValue as ActionItemPriority)
                : undefined;
            if (newPriority) {
              const updates = {
                priority: newPriority,
              } satisfies Partial<SerializedTodoItem>;
              nextGroups = nextGroups.map(g => ({
                ...g,
                items: g.items.map(it =>
                  it.id === event.itemId ? { ...it, ...updates } : it
                ),
              }));
              actionItemsService.updateById(event.itemId, updates);
            }
          }
        }

        // `event.nextGroups` is derived from the *rendered* groups; when we paginate
        // the UI we must merge it back into the full groups list so reordering does
        // not drop non-rendered groups.
        const baseGroups = groupsRef.current;
        const baseIds = new Set(baseGroups.map(g => g.id));
        const nextById = new Map(nextGroups.map(g => [g.id, g] as const));
        const mergedGroups = baseGroups.map(g => nextById.get(g.id) ?? g);
        for (const g of nextGroups) {
          if (!baseIds.has(g.id)) mergedGroups.push(g);
        }

        const ordered = mergedGroups.flatMap(g => g.items);
        setOptimisticPanelSnapshot(mergedGroups, ordered);

        ordered.forEach((item, idx) => {
          if (item.id) {
            actionItemsService.updateById(item.id, { priorityRank: idx + 1 });
          }
        });
      } finally {
        actionItemsService.endBatch();
      }
    },
    [actionItemsService, setOptimisticPanelSnapshot]
  );

  const requiredGroupIdsForFocus = useMemo(() => {
    if (!focusModeEnabled) return new Set<string>();
    const focusedIds = new Set(focusedItems.map((i: SerializedTodoItem) => i.id));
    const required = new Set<string>();
    for (const g of groups) {
      if (g.items.some(it => focusedIds.has(it.id))) {
        required.add(g.id);
      }
    }
    return required;
  }, [focusModeEnabled, focusedItems, groups]);

  const filteredGroups = useMemo(() => {
    const required = requiredGroupIdsForFocus;
    return groups.filter(
      (g, idx) => idx < visibleGroupCount || required.has(g.id)
    );
  }, [groups, requiredGroupIdsForFocus, visibleGroupCount]);

  const totalInGroups = groups.reduce((sum, g) => sum + g.items.length, 0);
  const visibleInGroups = filteredGroups.reduce(
    (sum, g) => sum + g.items.length,
    0
  );
  const hiddenInGroups = totalInGroups - visibleInGroups;

  const isEmpty = itemCounts.total === 0;

  return (
    <div className={styles.actionItemsPanel}>
      {loading ? (
        <ActionItemsLoading />
      ) : (
        <div className={styles.actionItemsContent}>
          <div className={styles.stickyHeader}>
            <AnimatePresence mode="wait" initial={false}>
              {headerMode === 'chart' ? (
                <motion.div
                  key="chart"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{
                    height: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
                    opacity: { duration: 0.15, delay: 0.1 },
                  }}
                  style={{ overflow: 'hidden', padding: '8px 0' }}
                >
                  <ActionItemsFocusChartLive />
                </motion.div>
              ) : (
                <motion.div
                  key="addItem"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{
                    height: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
                    opacity: { duration: 0.15, delay: 0.1 },
                  }}
                  style={{ overflow: 'hidden' }}
                  className={styles.addItemSection}
                >
                  <Suspense
                    fallback={
                      <div
                        style={{
                          width: '100%',
                          height: 39,
                          borderRadius: 8,
                          backgroundColor: 'var(--affine-border-color)',
                          opacity: 0.3,
                        }}
                      />
                    }
                  >
                    <LazyAddActionItemInline defaultCollapsed={false} />
                  </Suspense>
                </motion.div>
              )}
            </AnimatePresence>

            {showViewModeSelector ? (
              <div className={styles.categoryHeader}>
                <div className={styles.categoryHeaderContent}>
                  <Suspense fallback={<ViewModeSelectorLoading />}>
                    <LazyViewModeSelector
                      viewMode={viewMode}
                      onViewModeChange={handleViewModeChange}
                    />
                  </Suspense>
                </div>
              </div>
            ) : null}
          </div>

          <div ref={scrollViewportRef} className={styles.scrollableContent}>
            <AnimatePresence mode="wait">
              {isPicoFocusActive ? (
                <Suspense key="pico-focus" fallback={null}>
                  <LazyPicoFocusView focusedItems={focusedItems} />
                </Suspense>
              ) : null}
            </AnimatePresence>
            {isPicoFocusActive ? null : isKanbanMode ? (
              <Suspense
                fallback={
                  <div
                    style={{
                      height: 300,
                      backgroundColor: 'var(--affine-hover-color)',
                      borderRadius: 12,
                      opacity: 0.3,
                    }}
                  />
                }
              >
                <LazyKanbanBoard
                  items={flatRankItems}
                  onItemComplete={handleItemCompletion}
                />
              </Suspense>
            ) : (
              <div className={styles.actionItemsList}>
                <SortableActionItemsPanel
                  items={filteredGroups.flatMap(g => g.items)}
                  headerComponent={
                    <CompletionFilter
                      selectedFilter={completionFilter}
                      onFilterChange={handleCompletionFilterChange}
                      openCount={itemCounts.open}
                      closedCount={itemCounts.completed}
                      totalCount={itemCounts.total}
                      groupBy={isDueDateMode ? 'dueDate' : 'pageId'}
                      onGroupByChange={groupBy => setViewMode(groupBy)}
                    />
                  }
                  onReorder={() => {}}
                  onItemComplete={handleItemCompletion}
                  enableDragHandle={true}
                  focusedItems={focusedItems}
                  onSetFocusedItems={handleSetFocusedItems}
                  hideFocusedItems={completionFilter !== 'closed'}
                  selection={selection}
                  grouping={{
                    groupBy: viewMode as ActionItemGroupBy,
                    groups: filteredGroups,
                    onReorder: handleGroupedReorder,
                    allowCrossGroupDrag: isDueDateMode,
                    onGroupTitleClick: isMeetingMode
                      ? group => {
                          if (
                            group.metadata?.isDocumentGroup &&
                            group.metadata?.pageId
                          ) {
                            workbenchService.workbench.openDoc(
                              group.metadata.pageId,
                              { at: 'active' }
                            );
                          }
                        }
                      : undefined,
                  }}
                />
                {/* Sentinel for incremental group rendering (keeps DnD intact) */}
                <div
                  ref={loadMoreSentinelRef}
                  style={{ height: hasMoreGroups ? 1 : 0, width: '100%' }}
                  aria-hidden="true"
                />
                {isEmpty && (
                  <div className={styles.emptyListPlaceholder}>
                    <span className={styles.emptyListTitle}>
                      No action items yet
                    </span>
                    <span className={styles.emptyListSubtitle}>
                      Add your own, or let Pique automatically create and manage
                      them for you
                    </span>
                  </div>
                )}
                {focusModeEnabled && hiddenInGroups > 0 && (
                  <TruncationIndicator hiddenCount={hiddenInGroups} />
                )}
                {selectionActive && (
                  <div className={styles.floatingToolbarContainer}>
                    <div className={styles.floatingToolbarPill}>
                      <span className={styles.floatingToolbarCount}>
                        {selectedIds.length} selected
                      </span>
                      <button
                        type="button"
                        className={styles.floatingToolbarCloseButton}
                        onClick={handleClearSelection}
                        aria-label="Clear selection"
                      >
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 10 10"
                          fill="none"
                        >
                          <path
                            d="M1 1L9 9M9 1L1 9"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </div>
                    <div className={styles.floatingToolbarPill}>
                      <Tooltip
                        content={
                          completionFilter === 'open'
                            ? 'Mark as done'
                            : 'Reopen'
                        }
                        side="top"
                      >
                        <button
                          type="button"
                          className={styles.floatingToolbarIconButton}
                          onClick={handleBulkToggleComplete}
                          aria-label={
                            completionFilter === 'open'
                              ? 'Mark as done'
                              : 'Reopen'
                          }
                        >
                          {completionFilter === 'open' ? (
                            <svg
                              className={styles.floatingToolbarCheckIcon}
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M5 13l4 4L19 7"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M3 3v5h5"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </button>
                      </Tooltip>
                      <Tooltip content="Delete" side="top">
                        <button
                          type="button"
                          className={styles.floatingToolbarDangerIconButton}
                          onClick={handleBulkDelete}
                          data-testid="action-items-toolbar-delete"
                          aria-label="Delete"
                        >
                          <DeleteIcon style={{ width: 16, height: 16 }} />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
